import './TeamManager.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { ByzzerButton } from '@byzzer/ui-components';
import { useTenantApi } from '@/hooks/useTenantApi';
import CautionIcon from '@/images/icons/CautionIcon.svg';
import { confirm, openErrorModal } from '@/components/form/ByzzerModal';
import { openInvitationEditor } from "@/components/TeamManager/InvitationEditor";
import { openConsultantInvitationEditor } from "@/components/TeamManager/ConsultantInvitationEditor";
import { openManageTeamEditor } from "@/components/TeamManager/ManageTeamEditor";
import { openTeamEditor } from "@/components/TeamManager/TeamEditor";
import { openAddTeamEditor } from "@/components/TeamManager/AddTeamEditor";
import { openMemberEditor } from "@/components/TeamManager/MemberEditor";
import { openTeamRoleInfo } from "@/components/TeamManager/TeamRoleInfo";
import { openTeamInfo } from "@/components/TeamManager/TeamInfo";
import { TipIcon } from "@/components/icons";
import { ByzzerMask } from "@/components/ByzzerMask/ByzzerMask";
import { useUser } from "@/contexts/UserContext";
import { TrackClick } from "@/analytics";
import classnames from "classnames";
import { openMemberApprovalEditor } from "@/components/TeamManager/MemberApprovalEditor";
import { TeamMember } from '@/types/InvitationTypes';
import useIsMobile from '@/hooks/useIsMobile';
import { ByzzerTable } from '../ByzzerTable';
import { ByzColDef } from '@/types/TableTypes';

const memberTypeDisplayMapping = {
    'admin': 'Admin',
    'viewer': 'Viewer',
    'user': 'User',
}

const baseClassName = 'team-manager';

export type ConsultantPreset = { role: string; email: string; teamId: number };

function TeamManager() {
    const { createTeam, deleteTeam, getMyCompanySubscriptionUsage, getTeamMembers, getTeams, removeInvitation, removeUser, updateTeam } = useTenantApi();
    const { user, isCsrObo: isObo, oboRole } = useUser();
    const isMobile = useIsMobile();
    const [loading, setLoading] = useState(false);
    const [members, setMembers] = useState<TeamMember[]>([]);

    const [availableUserCredits, setAvailableUserCredits] = useState(0);
    const [canManage, setCanManage] = useState(user?.role?.toLowerCase() === 'admin');
    const [ready, setReady] = useState(true);
    const [teamOptions, setTeamOptions] = useState<{ display: string; value: number }[]>([]);
    const isSalesObo = isObo && oboRole === 'sales'
    useEffect(() => {
        setCanManage(user?.role?.toLowerCase() === 'admin')
    }, [user])
    useEffect(() => {
        loadData();
    }, []);

    async function onTeamRoleInfo() {
        openTeamRoleInfo()
    }

    async function onTeamInfo() {
        openTeamInfo()
    }

    async function loadData() {
        try {
            setLoading(true);

            const [usage, users, teams] = await Promise.all([
                getMyCompanySubscriptionUsage(),
                getTeamMembers(),
                getTeams()
            ])
            setAvailableUserCredits(usage.users?.limit - usage.users.used);
            setMembers(users.map(user => ({
                ...user,
                // join first and last name if either exists
                fullName: user.firstName?.trim?.() || user.lastName?.trim() ?
                    `${user.firstName} ${user.lastName}`.trim() : ''
            })).sort((a, b) => a.fullName?.localeCompare(b.fullName)));

            setTeamOptions(teams.map(({ id, displayName }) => ({
                value: id,
                display: displayName
            })));
        } catch (err: any) {
            openErrorModal({
                title: 'Uh oh',
                content: <>
                    <p>Something unexpected happened while sending your invitation.</p>
                </>,
                errorId: err.id
            })
        } finally {
            setLoading(false);
            setReady(true);
        }
    }

    async function onDeleteMember(member: TeamMember) {
        setLoading(true);
        const { configurationCounts, type, id } = member;
        const { countOfAlertsWithOtherRecipients, countOfScheduledSeries } = configurationCounts ?? {};
        const showAlertCount = Boolean(countOfAlertsWithOtherRecipients);
        const showSeriesCount = Boolean(countOfScheduledSeries);
        const showCountMessage = showAlertCount || showSeriesCount;
        try {
            if (
                await confirm({
                    closeButton: true,
                    className: 'subscription-users__remove-container',
                    content: (
                        <div className="subscription-users__remove">
                            <div className="subscription-users__remove-header">
                                <img src={CautionIcon} alt={'!'} />
                                <span>Delete Member</span>
                            </div>
                            <div className="subscription-users__remove-subtitle">
                                Are you sure you want to delete this member? {!showCountMessage && <p>This action cannot be undone.</p>}
                            </div>
                            {Number(showCountMessage) > 0 && <div className="subscription-users__remove-content">
                                <p>By proceeding, you will not receive any future deliveries associated with this member:</p>
                                <ul className='subscription-users__remove-list'>
                                    {showAlertCount && <li>{countOfAlertsWithOtherRecipients} Alerts that other members receive</li>}
                                    {showSeriesCount && <li>{countOfScheduledSeries} Scheduled Data On Demand Series</li>}
                                </ul>
                                <p>This action cannot be undone. Please review carefully before confirming deletion.</p>
                            </div>}
                        </div>
                    ),
                    yesLabel: 'Remove',
                    noLabel: 'Cancel',
                })
            ) {
                if (type === 'invitation') {

                    await removeInvitation(id);
                } else {
                    await removeUser(id);
                }
                await loadData();
            }
        } catch (err: any) {
            // tbd
        } finally {
            setLoading(false);
        }
    }

    async function onEditMember(member: TeamMember) {
        let result;
        // if (!member?.isConsultant) {
        //     result = await openMemberEditor({
        //         // @ts-ignore
        //         user: {
        //             role: member.role,
        //             email: member.email,
        //             teamId: member.teamId,
        //         },
        //         teamOptions
        //     });
        // } else {
        try {
            setLoading(true);
            const teams = await getTeams()
            const mappedTeams = teams.map(({ id, displayName }) => ({
                value: id,
                display: displayName
            }));
            setTeamOptions(mappedTeams);
            setLoading(false);
            result = await openMemberEditor({
                // @ts-ignore
                user: member,
                teamOptions: mappedTeams
            });
        } catch (err: any) {
            openErrorModal({
                title: 'Uh oh',
                content: <>
                    <p>Something unexpected happened while sending your invitation.</p>
                </>,
                errorId: err.id
            })
            setLoading(false);
        }

        switch (result) {
            case 'updated':
                return loadData();
            case 'delete':
                return onDeleteMember(member);
            default:
        }
    }

    async function onApproveMember(member: TeamMember) {

        try {
            setLoading(true);
            const teams = await getTeams()
            const mappedTeams = teams.map(({ id, displayName }) => ({
                value: id,
                display: displayName
            }));
            setTeamOptions(mappedTeams);
            setLoading(false);

            if (await openMemberApprovalEditor({
                // @ts-ignore
                member,
                teamOptions: mappedTeams
            })) {
                setLoading(true);
                try {
                    await loadData();
                } catch (err) {
                    // tbd
                } finally {
                    setLoading(false);
                }
            }
        } catch (err: any) {
            openErrorModal({
                title: 'Uh oh',
                content: <>
                    <p>Something unexpected happened while sending your invitation.</p>
                </>,
                errorId: err.id
            })
            setLoading(false);
        }
    }

    async function onAddMemberClick() {
        const inviteResult: false | undefined | { type: 'consultant'; data?: ConsultantPreset; } = await openInvitationEditor({
            // @ts-ignore
            teamOptions, availableUserCredits, members
        })
        if (inviteResult && inviteResult?.type === 'consultant') {
            await handleAddConsultant(inviteResult?.data);
        } else if (inviteResult) {
            await loadData();
        }
    }

    async function handleAddConsultant(preset?: ConsultantPreset) {
        if (await openConsultantInvitationEditor({
            // @ts-ignore
            teamOptions, availableUserCredits, members, preset
        })) { // if it resolves as truthy, do the needful
            await loadData();
        }
    }

    async function onManageTeamClick() {
        const result = await openManageTeamEditor({
            // @ts-ignore
            teamOptions
        })
        if (result.function === "delete") {
            await onTeamDeleteClick(result.team)
        } else if (result.function === "close") {
            // We shouldnt need to reload the data here, as the user is just closing the modal
            // await loadData(); 
        } else if (result.function === "edit") {
            await onTeamEditClick(result.team)
        } else if (result.function === 'add') {
            await onTeamAddClick()
        }
    }

    async function teamCreation(count, teams) {
        if (count > 1) {
            try {
                await createTeam({ displayName: teams[0] })
                teams.shift();
                count = count - 1;
                await teamCreation(count, teams)
                await loadData()
            } catch (err) {
                console.log('error creating/loading team(s)', err)
            } finally {
                setLoading(false);
            }
        } else {
            try {
                await createTeam({ displayName: teams[0] })
                await loadData()
            } catch (err) {
                console.log('error creating/loading team(s)', err)
            } finally {
                setLoading(false);
            }
        }
    }

    async function onTeamAddClick() {
        const result = await openAddTeamEditor({
            // @ts-ignore
            teamOptions
        })
        if (result.function === "close") {
            await loadData();
        } else if (result.function === "add") {
            let count = result?.newTeams.length
            let teams = result?.newTeams
            if (count > 0) {
                setLoading(true);
                teamCreation(count, teams)
            }
        }
    }

    async function onTeamEditClick(team) {
        const result = await openTeamEditor({
            // @ts-ignore
            team, teamOptions
        })
        if (result.function === "close") {
            await loadData();
        } else if (result.function === "edit") {
            await updateTeam(result.team.id, { id: result.team.id, displayName: result.team.newTitle });
            await loadData();
        }
    }

    async function onTeamDeleteClick(team) {
        if (await confirm({
            closeButton: true,
            className: "subscription-users__remove-container",
            content: <div className="subscription-users__remove">
                <img className="subscription-users__remove-icon" src={CautionIcon} alt={"!"} />
                <div className="subscription-users__remove-title">Remove Team</div>
                <p>Are you sure you want to delete the team, "{team.display}"?</p>
                <br />
                <p>If you delete this team move all current members to a new team.</p>
                <p>
                    If report runs are allocated by team and a member is not part of a team, the member will not be able
                    to run a report.
                </p>
            </div>,
            yesLabel: 'Yes, Remove',
            noLabel: 'No'
        })) {
            try {
                setLoading(true);
                await deleteTeam(team.value)
                await loadData()
            } catch (err) {
                console.log('error deleting/loading team(s)', err)
            } finally {
                setLoading(false);
            }
        } else {
            await onManageTeamClick()
        }
    }

    if (!ready) {
        return <></>
    }

    const memberCount = members.length;

    const defaultTeamMemberColDef = useMemo(
        () => ({
            filter: true, // make every column use 'text' filter by default
            sortable: true,
            floatingFilter: true, // enable floating filters by default
            resizable: true, //set each col resizable
            width: 200, // set every column width
            flex: 0,
            autoHeight: false,
            wrapText: false,
            suppressMenu: true,
            lockVisible: true,
        }),
        []
    );
    type TeamMemberColId = 'name' | 'email' | 'memberType' | 'team' | 'status' | 'actions';

    const getTeamMemberStatus = (member: TeamMember) => {
        const { type, isPending, metadata } = member ?? {};
        if (type === 'user' && isPending) {
            return 'Pending Approval';
        } else if (metadata?.isInvitationPendingApproval && metadata?.isInvitationDismissed) {
            return 'Not Approved by NIQ';
        } else if (metadata?.isInvitationPendingApproval) {
            return 'Pending NIQ Approval';
        } else if (type === 'user') {
            return 'Active';
        } else if (type === 'invitation') {
            return 'Pending Accept';
        } else {
            return '--';
        }
    }

    const [teamMemberColumnDefs] = useState<ByzColDef<TeamMember, TeamMemberColId>[]>([
        {
            colId: 'name',
            headerName: 'Name',
            width: 300,
            valueGetter: ({ data }) => data.fullName,
            tooltipValueGetter: ({ data }) => data.fullName,
        },
        {
            colId: 'email',
            headerName: 'Email',
            width: 350,
            valueGetter: ({ data }) => data.email,
            tooltipValueGetter: ({ data }) => data.email
        },
        {
            colId: 'memberType',
            headerName: 'Member Type',
            width: 200,
            valueGetter: ({ data }) => {
                return memberTypeDisplayMapping[data.role] || data.role || '--';
            },
            tooltipValueGetter: ({ data }) => {
                return memberTypeDisplayMapping[data.role] || data.role || '--';
            },
        },
        {
            colId: 'team',
            headerName: 'Team',
            width: 250,
            valueGetter: ({ data }) => {
                return data.teamName ?? '--';
            },
            tooltipValueGetter: ({ data }) => {
                return data.teamName ?? '--';
            },
        },
        {
            headerName: 'Status',
            colId: 'status',
            width: 250,
            valueGetter: ({ data }) => getTeamMemberStatus(data),
            cellRenderer: (params) => {
                const { data } = params;
                return <TeamMemberStatus member={data} />
            },
        },
        {
            headerName: 'Actions',
            colId: 'actions',
            suppressMenu: true,
            sortable: false,
            filter: false,
            floatingFilter: false,
            resizable: false,
            width: 130,
            cellRenderer: (params) => {
                const { data: member } = params;
                return (
                    <>
                        {canManage && !isSalesObo && (
                            <TeamMemberActions
                                member={member}
                                onApprove={onApproveMember}
                                onEdit={onEditMember}
                                onDelete={onDeleteMember} />
                        )}
                    </>
                );
            },
        },
    ]);

    return (
        <div className={`${baseClassName}`}>
            <ByzzerMask show={loading} loading={loading} />
            <div className={`${baseClassName}__heading`}>
                <div className={`${baseClassName}__summary`}>
                    <div className={`${baseClassName}__title`}>Team Members</div>
                    <div className={`${baseClassName}__notes`}>
                        {(canManage && availableUserCredits > 0) && (<>
                            There {memberCount === 1 ? 'is' : 'are'} currently {memberCount} member{memberCount === 1 ? '' : 's'} on your subscription. You can add {availableUserCredits} more.
                        </>)}
                        {/* using less than 1 because of the possibility that more credits where used than are available, it can happen */}
                        {canManage && availableUserCredits < 1 && (
                            <>
                                <b>Your company subscription currently has {memberCount} members, and all available admin
                                    and user seats have been filled.</b> <br />To add more users, you can remove inactive or
                                pending members or contact your sales representative to purchase additional seats.
                                <br />You can also add unlimited viewers to your account at any time.
                            </>
                        )}
                    </div>
                </div>
            </div>

            <ByzzerTable
                rowData={members} // Row Data for Rows
                columnDefs={teamMemberColumnDefs} // Column Defs for Columns
                defaultColDef={defaultTeamMemberColDef} // Column Defs for Columns
                tableArea={'teamMembers'}
                enableColumnHideShow={true}
                enableSaveLayout={true}
                enableSaveSort={true}
            />

            <div className={`${baseClassName}__actions`}>
                {canManage && (
                    <>
                        {!isSalesObo && (
                            <TrackClick name={'Sub Members Manage Members clicked'}>
                                <ByzzerButton onClick={onManageTeamClick}>Manage Team</ByzzerButton>
                            </TrackClick>
                        )}
                        {/* <ByzzerButton onClick={handleAddConsultant}>Add A Third Party</ByzzerButton> */}
                        <TrackClick name={'Sub Members Add Team clicked'}>
                            <ByzzerButton onClick={onAddMemberClick}>Add A Member</ByzzerButton>
                        </TrackClick>
                    </>
                )}
            </div>
        </div>
    );
}

// todo: convert this to popup menu
function TeamMemberActions({ member, onEdit, onApprove, onDelete, ...props }) {

    const baseClassName = 'team-member-actions';
    const [showEdit, setShowEdit] = useState(false);
    const [showApprove, setShowApprove] = useState(false)

    useEffect(() => {
        setShowEdit(member?.isPending === false);
        setShowApprove(member?.type === 'user' && member?.isPending === true);
    }, [member])

    return <div className={`${baseClassName}`}>
        {showEdit && (
            <div className={classnames(`${baseClassName}__action`, `${baseClassName}__action--edit`)}
                onClick={() => onEdit(member)} />
        )}
        {showApprove && (
            <div className={classnames(`${baseClassName}__action`, `${baseClassName}__action--approve`)}
                onClick={() => onApprove(member)} />
        )}
        <div className={classnames(`${baseClassName}__action`, `${baseClassName}__action--delete`)}
            onClick={() => onDelete(member)} />
    </div>
}


function TeamMemberStatus({ member }: { member: TeamMember }) {

    const baseClassName = `team-member-status`;
    const [label, setLabel] = useState('--');
    const [stateSuffix, setStateSuffix] = useState('')

    useEffect(() => {

        const { type, isPending, metadata } = member ?? {};
        if (type === 'user' && isPending) {
            setLabel('Pending Approval');
            setStateSuffix('pending-approval')
        } else if (metadata?.isInvitationPendingApproval && metadata?.isInvitationDismissed) {
            setLabel('Not Approved by NIQ');
            setStateSuffix('pending-approval')
        } else if (metadata?.isInvitationPendingApproval) {
            setLabel('Pending NIQ Approval');
            setStateSuffix('pending-approval')
        } else if (type === 'user') {
            setLabel('Active');
            setStateSuffix('active');
        } else if (type === 'invitation') {
            setLabel('Pending Accept')
            setStateSuffix('pending-acceptance')
        } else {
            setLabel('--');
            setStateSuffix('');
        }
    }, [member])
    return <div className={classnames(baseClassName, `${baseClassName}--${stateSuffix}`)}>{label}</div>
}


export default TeamManager;
